import React, { useState } from 'react';
import { Card, CardHeader, Heading, CardBody, Text, VStack, HStack, Box, useColorMode } from '@chakra-ui/react';
import { RotateCw } from 'lucide-react';
import { useDispatch } from 'react-redux';
import { getSalesPaymentReceiveReport } from '../../../../redux/actions/dashboard';
import ReactApexChart from 'react-apexcharts';

const SalesPaymentRadialChart = ({ data, height, financialAnalysisDateRange, user }) => {
   const { colorMode } = useColorMode();
   const isDarkMode = colorMode === 'dark';
   const [isRotating, setIsRotating] = useState(false);
   const dispatch = useDispatch();

   if (!data || data.length === 0) return null;

   const entry = data[0];
   const salesAmount = parseFloat(entry.total_sales_amount);
   const paidAmount = parseFloat(entry.total_paid_amount);
   const unpaidAmount = salesAmount - paidAmount;

   const options = {
      chart: {
         type: 'radialBar',
         offsetY: -10,
      },
      plotOptions: {
         radialBar: {
            startAngle: 0,
            endAngle: 360,
            hollow: {
               size: '55%',
            },
            dataLabels: {
               name: {
                  fontSize: '16px',
                  color: isDarkMode ? 'white' : undefined,
               },
               value: {
                  fontSize: '18px',
                  color: isDarkMode ? 'white' : undefined,
                  formatter: (val) => `${val}%`,
               },
               total: {
                  show: true,
                  label: 'Paid %',
                  color: isDarkMode ? 'white' : undefined,
                  formatter: () => `${((paidAmount / salesAmount) * 100).toFixed(2)}%`,
               },
            },
            track: {
               background: isDarkMode ? '#2D3748' : undefined,
            },
         },
      },
      labels: ['Paid', 'Unpaid'],
      colors: ['#38A169', '#E53E3E'], // Keeping your original colors
      series: [((paidAmount / salesAmount) * 100).toFixed(2), ((unpaidAmount / salesAmount) * 100).toFixed(2)],
   };

   const handleReload = async () => {
      setIsRotating(true);
      dispatch(
         getSalesPaymentReceiveReport({
            companyId: user?.loginhistory?.companyId,
            fromDate: financialAnalysisDateRange.fromDate,
            toDate: financialAnalysisDateRange.toDate,
         }),
      );
      setTimeout(() => setIsRotating(false), 700);
   };

   return (
      <Card h={height + 80} minHeight={440}>
         <CardHeader display="flex">
            <Heading size="md">Sales vs Payment — {entry.period_label}</Heading>
            <button
               onClick={handleReload}
               className={`ml-auto p-2 rounded-full transition-all hover:scale-110 ${
                  isDarkMode ? 'hover:bg-gray-600 text-blue-400' : 'hover:bg-gray-100 text-blue-600'
               }`}>
               <RotateCw
                  className={`w-5 h-5 transform ${
                     isRotating ? 'rotate-[360deg]' : ''
                  } transition-transform ease-in-out`}
                  style={{
                     transitionDuration: isRotating ? '700ms' : '10ms',
                  }}
               />
            </button>
         </CardHeader>
         <CardBody>
            <HStack spacing={10} justify="center">
               <Box w={'100%'}>
                  <ReactApexChart
                     options={options}
                     series={[((paidAmount / salesAmount) * 100).toFixed(2)]}
                     type="radialBar"
                     height={height}
                  />
               </Box>
               <VStack align="start" spacing={3}>
                  <Text>
                     <strong>Total Sales:</strong> ₹{salesAmount.toLocaleString('en-IN', { maximumFractionDigits: 2 })}
                  </Text>
                  <Text>
                     <strong>Total Paid:</strong> ₹{paidAmount.toLocaleString('en-IN', { maximumFractionDigits: 2 })}
                  </Text>
                  <Text>
                     <strong>Unpaid:</strong> ₹{unpaidAmount.toLocaleString('en-IN', { maximumFractionDigits: 2 })}
                  </Text>
               </VStack>
            </HStack>
         </CardBody>
      </Card>
   );
};

export default SalesPaymentRadialChart;
